import * as React from "react";
import { useMemo } from "react";
import { useDiscussHistory } from "@/hooks/useDiscussHistory";
import { useSendMessage } from "@/hooks/useSendMessage";
import { useDocument } from "@/hooks/useDocument";
import { useUser } from "@/hooks/useUser";
import { CompassSocketProvider, useCompassSocketContext } from "@/context/CompassSocketContext";
import { DiscussContentView } from "./DiscussContentView";
import { buildWelcomeMessage, useDiscussHandlers } from "./Discuss.utils";

interface DiscussContentProps {
  showCompassDialog: boolean;
  setShowCompassDialog: (show: boolean) => void;
  onNewMessage?: () => void;
}

const DiscussContent: React.FC<DiscussContentProps> = ({
  showCompassDialog,
  setShowCompassDialog,
  onNewMessage,
}) => {
  const lastMessageRef = React.useRef<HTMLDivElement>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  const { data: doc } = useDocument();
  const { user } = useUser();
  const { taskStatuses, activeProcessingTask } = useCompassSocketContext();
  const isCompassProcessing = Object.values(taskStatuses).some((status) => status === "processing");
  const welcomeMessage = useMemo(() => buildWelcomeMessage(user?.first_name), [user?.first_name]);

  const {
    data: historyData,
    isLoading: isHistoryLoading,
    error: historyError,
  } = useDiscussHistory(doc?._id);
  const { mutate: sendMessage, isPending: isSendingMessage } = useSendMessage(doc?._id);
  const { handleSendMessage, handleRelatedQuestionClick } = useDiscussHandlers(
    doc,
    sendMessage,
    onNewMessage
  );

  React.useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [historyData?.prev_chat, isSendingMessage, isCompassProcessing]);

  return (
    <DiscussContentView
      isHistoryLoading={isHistoryLoading}
      historyError={historyError}
      historyData={historyData}
      isSendingMessage={isSendingMessage}
      isCompassProcessing={isCompassProcessing}
      activeProcessingTask={activeProcessingTask}
      welcomeMessage={welcomeMessage}
      lastMessageRef={lastMessageRef}
      scrollContainerRef={scrollContainerRef}
      doc={doc}
      showCompassDialog={showCompassDialog}
      setShowCompassDialog={setShowCompassDialog}
      onSendMessage={handleSendMessage}
      onRelatedQuestionClick={handleRelatedQuestionClick}
    />
  );
};

const Discuss: React.FC<{ onNewMessage?: () => void }> = ({ onNewMessage }) => {
  const [showCompassDialog, setShowCompassDialog] = React.useState(true);
  const dialogAutoClosedRef = React.useRef(false);

  const handleProcessingStart = React.useCallback(() => {
    if (!dialogAutoClosedRef.current) {
      setShowCompassDialog(false);
      dialogAutoClosedRef.current = true;
    }
    onNewMessage?.();
  }, [onNewMessage]);

  return (
    <CompassSocketProvider onMessage={handleProcessingStart}>
      <DiscussContent
        showCompassDialog={showCompassDialog}
        setShowCompassDialog={setShowCompassDialog}
        onNewMessage={onNewMessage}
      />
    </CompassSocketProvider>
  );
};

export default Discuss;
